/**
 * `useTasklistGate` — is the Pendências surface available?
 *
 * The runtime gates the whole feature on `LEX_TASKLIST_ENABLE`; when unset the
 * gateway methods are simply absent. The handoff requires the section to HIDE
 * entirely in that case, with no error — a lawyer on a gate-off runtime should
 * see nothing, not a broken pane.
 *
 * There is no capability flag on the wire, so availability is inferred the way
 * the sibling Contacts surface infers it: probe once and treat a
 * method-not-found rejection as "gate off". This is also what covers a runtime
 * that simply does not serve `tasklist.*` — the section hides rather than the
 * SPA faking a local store.
 */
import { useEffect, useState } from "react";

import { useGatewayStore } from "@/stores/useGatewayStore";
import { listTasks } from "@/api/lx-tasklist";

export type TasklistGateState = "loading" | "enabled" | "disabled";

/** Matches the runtime's "no such method" rejection (mirrors ContactsLayout). */
function isMethodNotFound(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /method|unknown|not.?found|unsupported|disabled/i.test(msg);
}

export function useTasklistGate(): TasklistGateState {
  const [state, setState] = useState<TasklistGateState>("loading");
  const connectionState = useGatewayStore((s) => s.connectionState);

  useEffect(() => {
    if (connectionState !== "connected") return;
    let cancelled = false;
    void (async () => {
      try {
        await listTasks({});
        if (!cancelled) setState("enabled");
      } catch (err) {
        if (cancelled) return;
        // Gate off ⇒ hide. A transient/other error is NOT a gate signal —
        // leave the surface enabled and let the pane's own error state show,
        // rather than making the whole section vanish on a blip.
        setState(isMethodNotFound(err) ? "disabled" : "enabled");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [connectionState]);

  return state;
}
